import React, { useEffect, useMemo } from 'react';
import DataAnalyleStyled from './DataAnalyleStyled';
import { Box } from '@material-ui/core';
import { useDispatch, useSelector } from 'dva';
import DataTableNativeML from '@/components/DataTableNativeML';
import { fmtThousand } from '@/utils/comUtil';
import Bar from './echarts/Bar';
import Pie from './echarts/Pie';
import CouponRate from './echarts/CouponRate';
import VisitedDay from './echarts/VisitedDay';
import NewMemberRegister from './echarts/NewMemberRegister';
import MemberVisited from './echarts/MemberVisited';
import FootwearFive from './echarts/FootwearFive';
// import PacketTopFive from './echarts/PacketTopFive';
import RegisterDay from './echarts/RegisterDay';

// index 标识与中文表头映射
const INDEX_NAME_MAP = {
  PV: 'PV',
  UV: 'UV',
  REGISTER: '注册',
  REGISTER_RATE: '注册转化率',
  BUY: '购买',
  BUY_RATE: '购买转化率',
};

// 需要按百分比格式化的指标集合
const RATE_INDEX_SET = new Set(['REGISTER_RATE', 'BUY_RATE', 'BUY_RATE_DATA']);

// 按 index 过滤并按 SORT 升序排序
const filterAndSortByIndex = (source, index) => {
  if (!source) {
    return [];
  }
  const list = source.filter((item) => item.index === index);
  if (list.length > 0) {
    list.sort((a, b) => a.values[2].value - b.values[2].value);
  }
  return list;
};

// 将同 index 的多条数据合并为一行表格数据（key 为 label，value 为格式化后的值）
const initData = (arr) => {
  if (!arr || arr.length === 0) {
    return {};
  }
  const obj = {};
  let name = '';
  arr.forEach((item) => {
    const keyVal = item.values[0].value;
    const rawValue = item.values[1].value;
    obj[keyVal] = RATE_INDEX_SET.has(item.index)
      ? `${(rawValue * 100).toFixed(2)}%`
      : fmtThousand(rawValue);
    name = INDEX_NAME_MAP[item.index] || name;
  });
  obj.name = name;
  return obj;
};

// 遍历指标配置，生成表格行数据（无数据时使用 fallback 名称占位）
const buildTableData = (metrics) => {
  return metrics.map(({ list, fallbackName }) =>
    list && list.length > 0 ? initData(list) : { name: fallbackName },
  );
};

// 根据所有行的 keys 生成表格列（收集全部 label，排除 name 和“全部”）
const buildColumns = (rows) => {
  const labelSet = new Set();
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((k) => {
      if (k !== 'name' && k !== '全部') {
        labelSet.add(k);
      }
    });
  });
  const dynamicCols = Array.from(labelSet).map((columnItem) => ({
    // 表头列宽较窄，长文本用 inline-block 限制宽度后才能换行
    // 76px = 列宽 100px - th 左右 padding（8px + 16px）
    title: (
      <span
        style={{
          display: 'inline-block',
          maxWidth: '76px',
          wordBreak: 'break-all',
          lineHeight: '20px',
          verticalAlign: 'middle',
        }}
      >
        {columnItem}
      </span>
    ),
    id: columnItem,
    align: 'center',
    width: '100',
  }));
  return [
    {
      id: 'name',
      width: '100',
      align: 'center',
      render: (record) =>
        record.name && (
          <span style={{ color: '#212121', fontWeight: '600' }}>
            {record.name}
          </span>
        ),
    },
    {
      id: '全部',
      title: dynamicCols.length !== 0 ? '全部' : '',
      width: '100',
      align: 'center',
      render: (record) =>
        record['全部'] && (
          <span style={{ color: '#1565C0', fontWeight: '400' }}>
            {record['全部']}
          </span>
        ),
    },
    ...dynamicCols,
  ];
};

const DataAnalyle = (props) => {
  const { code } = props || {};
  const dispatch = useDispatch();
  const loadingEffect = useSelector((state) => state.loading);
  const effects = loadingEffect.effects;
  const {
    registerShopSort,
    buyShopSort,
    performSort,
    memberSpread,
    cancellateUseRate,
    cancellateRate,
    visitedDay,
    registerDay,
    newMemberRegister,
    visitedMemberSall,
  } = useSelector((state) => state.analyse);

  // 页面初始化：批量触发所有数据接口请求
  useEffect(() => {
    const dispatchAction = (type) => {
      dispatch({
        type,
        payload: { values: { code } },
      });
    };
    [
      'analyse/registerShopSort',
      'analyse/buyShopSort',
      'analyse/performSort',
      'analyse/memberSpread',
      'analyse/cancellateUseRate',
      'analyse/cancellateRate',
      'analyse/registerDay',
      'analyse/visitedDay',
      'analyse/newMemberRegister',
      'analyse/visitedMemberSall',
    ].forEach(dispatchAction);
  }, [code, dispatch]);

  // 新会员注册+销售追踪表格的指标配置
  const newMemberMetrics = useMemo(
    () => [
      {
        list: filterAndSortByIndex(newMemberRegister, 'PV'),
        fallbackName: 'PV',
      },
      {
        list: filterAndSortByIndex(newMemberRegister, 'UV'),
        fallbackName: 'UV',
      },
      {
        list: filterAndSortByIndex(newMemberRegister, 'REGISTER'),
        fallbackName: '注册',
      },
      {
        list: filterAndSortByIndex(newMemberRegister, 'REGISTER_RATE'),
        fallbackName: '注册转化率',
      },
      {
        list: filterAndSortByIndex(newMemberRegister, 'BUY'),
        fallbackName: '购买',
      },
      {
        list: filterAndSortByIndex(newMemberRegister, 'BUY_RATE'),
        fallbackName: '购买转化率',
      },
    ],
    [newMemberRegister],
  );

  // 访问会员销售追踪表格的指标配置
  const visitedMetrics = useMemo(
    () => [
      {
        list: filterAndSortByIndex(visitedMemberSall, 'PV'),
        fallbackName: 'PV',
      },
      {
        list: filterAndSortByIndex(visitedMemberSall, 'UV'),
        fallbackName: 'UV',
      },
      {
        list: filterAndSortByIndex(visitedMemberSall, 'BUY'),
        fallbackName: '购买',
      },
      {
        list: filterAndSortByIndex(visitedMemberSall, 'BUY_RATE'),
        fallbackName: '购买转化率',
      },
    ],
    [visitedMemberSall],
  );

  // 生成两张表格的行数据与列定义
  const arrayDataNewMember = useMemo(
    () => buildTableData(newMemberMetrics),
    [newMemberMetrics],
  );
  const arrayDataVisited = useMemo(
    () => buildTableData(visitedMetrics),
    [visitedMetrics],
  );

  const columnsNewMember = useMemo(
    () => buildColumns(arrayDataNewMember),
    [arrayDataNewMember],
  );
  const columnsVisited = useMemo(
    () => buildColumns(arrayDataVisited),
    [arrayDataVisited],
  );

  // 各接口的 loading 状态
  const loading = effects['analyse/newMemberRegister'];
  const loading1 = effects['analyse/visitedMemberSall'];
  const loading2 = effects['analyse/registerShopSort'];
  const loading3 = effects['analyse/buyShopSort'];
  const loading4 = effects['analyse/performSort'];
  // const loading5 = effects['analyse/packetSort'];
  const loading6 = effects['analyse/memberSpread'];
  const loading7 = effects['analyse/queryCouponUsedPercentage'];
  const loading9 = effects['analyse/cancellateUseRate'];
  const loading10 = effects['analyse/registerDay'];
  const loading11 = effects['analyse/visitedDay'];

  return (
    <DataAnalyleStyled>
      <Box className="main_contain">
        <Box className="main_contain_left">
          <Box className="visited_day">
            <VisitedDay
              data={visitedDay}
              title="日访问趋势"
              loading11={loading11}
            ></VisitedDay>
          </Box>
          <Box className="register_day">
            <RegisterDay
              data={registerDay}
              title="日注册趋势"
              loading10={loading10}
            ></RegisterDay>
          </Box>
        </Box>
        <Box className="main_contain_right">
          <Box className="register_shop_sort">
            <Bar
              data={registerShopSort}
              title="注册UTMTOP5"
              loading2={loading2}
            ></Bar>
          </Box>
          <Box className="buy_shop_sort">
            <Bar
              data={buyShopSort}
              title="购买UTMTOP5"
              loading3={loading3}
            ></Bar>
          </Box>
          <Box className="new_old_member_spread">
            <Pie
              data={memberSpread}
              title="新老会员参与分布"
              loading6={loading6}
            ></Pie>
          </Box>
        </Box>
      </Box>
      {/* 新会员注册+销售追踪：左图表 + 右数据表 */}
      <Box className="new_member_register">
        <Box className="register_left">
          <NewMemberRegister
            loading={loading}
            data={newMemberRegister}
            title="新会员注册+销售追踪"
            REGISTER_RATECOPY={filterAndSortByIndex(
              newMemberRegister,
              'REGISTER_RATE',
            )}
            BUY_RATECOPY={filterAndSortByIndex(newMemberRegister, 'BUY_RATE')}
          ></NewMemberRegister>
        </Box>
        <Box className="register_right">
          <DataTableNativeML
            loading={loading}
            pageButton={false}
            columns={columnsNewMember}
            data={arrayDataNewMember}
            paging={false}
            devicesMinWidth="auto"
          ></DataTableNativeML>
        </Box>
      </Box>
      {/* 访问会员销售追踪：左漏斗图 + 右数据表 */}
      <Box className="visited_member_sall">
        <Box className="sell_left">
          <MemberVisited
            loading1={loading1}
            data={visitedMemberSall}
            title="访问会员销售追踪"
            BUY_RATE_DATACOPY={filterAndSortByIndex(
              visitedMemberSall,
              'BUY_RATE',
            )}
          ></MemberVisited>
        </Box>
        <Box className="sell_right">
          <DataTableNativeML
            loading1={loading1}
            pageButton={false}
            columns={columnsVisited}
            data={arrayDataVisited}
            paging={false}
            devicesMinWidth="auto"
          ></DataTableNativeML>
        </Box>
      </Box>
      <Box className="coupon">
        <Box className="cancellate_rate">
          <CouponRate
            data={cancellateRate}
            title="优惠券核销率"
            loading9={loading9}
          ></CouponRate>
        </Box>
        <Box className="cancellate_use_rate">
          <Pie
            data={cancellateUseRate}
            title="优惠券使用占比"
            loading7={loading7}
          ></Pie>
        </Box>
      </Box>
      <Box className="clothing">
        <Box className="shoe_sort">
          <FootwearFive
            data={performSort}
            title="剧目TOP5"
            loading4={loading4}
            unit="张"
          ></FootwearFive>
        </Box>
        {/* <Box className="packet_sort">
          <PacketTopFive
            data={packetSort}
            title="包类TOP5"
            loading5={loading5}
            unit="只"
          ></PacketTopFive>
        </Box> */}
      </Box>
    </DataAnalyleStyled>
  );
};
export default DataAnalyle;
